// src/components/SeatsBadge.jsx
// Badge showing seat usage for the organization

import React from "react";
import { Box, Typography, Tooltip, CircularProgress } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import { useSubscription } from "../context/SubscriptionContext";

/**
 * SeatsBadge - Shows seat usage in a compact badge format
 *
 * Color coding:
 * - Green: More than 1 seat available
 * - Yellow: Only 1 seat available
 * - Red: No seats available (full)
 *
 * @param {Object} props
 * @param {boolean} props.showIcon - Whether to show the people icon (default: true)
 * @param {"small" | "medium"} props.size - Badge size (default: "small")
 */
export default function SeatsBadge({ showIcon = true, size = "small" }) {
  const theme = useTheme();
  const { seats, loading } = useSubscription();

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <CircularProgress size={14} />
      </Box>
    );
  }

  if (!seats) return null;

  const { seats_used, seat_limit, seats_available } = seats;

  // Determine color based on availability
  const getStatusColor = () => {
    if (seats_available === 0) return theme.palette.error;
    if (seats_available === 1) return theme.palette.warning;
    return theme.palette.success;
  };

  const statusColor = getStatusColor();
  const isSmall = size === "small";

  const tooltipContent = seats_available === 0
    ? "No seats available. Upgrade to add more team members."
    : seats_available === 1
    ? "1 seat remaining"
    : `${seats_available} seats available`;

  return (
    <Tooltip title={tooltipContent} arrow>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: isSmall ? 1 : 1.5,
          py: isSmall ? 0.25 : 0.5,
          borderRadius: 1,
          bgcolor: alpha(statusColor.main, 0.12),
          color: statusColor.main,
          cursor: "default",
        }}
      >
        {showIcon && (
          <PeopleRoundedIcon
            sx={{ fontSize: isSmall ? 14 : 18 }}
          />
        )}
        <Typography
          variant={isSmall ? "caption" : "body2"}
          fontWeight={600}
          sx={{ lineHeight: 1 }}
        >
          {seats_used}/{seat_limit}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/**
 * SeatsProgressBar - Shows seat usage as a progress bar
 */
export function SeatsProgressBar({ showLabel = true }) {
  const theme = useTheme();
  const { seats, loading } = useSubscription();

  if (loading || !seats) return null;

  const { seats_used, seat_limit, seats_available } = seats;
  const percentage = (seats_used / seat_limit) * 100;

  // Determine color based on usage
  const getBarColor = () => {
    if (seats_available === 0) return theme.palette.error.main;
    if (seats_available === 1) return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  return (
    <Box sx={{ width: "100%" }}>
      {showLabel && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Team Members
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {seats_used} / {seat_limit}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          width: "100%",
          height: 6,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${percentage}%`,
            height: "100%",
            borderRadius: 3,
            bgcolor: getBarColor(),
            transition: "width 0.3s ease",
          }}
        />
      </Box>
      {seats_available <= 1 && (
        <Typography
          variant="caption"
          color={seats_available === 0 ? "error" : "warning.main"}
          sx={{ mt: 0.5, display: "block" }}
        >
          {seats_available === 0
            ? "No seats available"
            : "1 seat remaining"}
        </Typography>
      )}
    </Box>
  );
}
